import { useState, useEffect, useCallback } from 'react';
import { Plus, Trash2, X, Copy, ExternalLink, Link2, Check, ChevronDown, ChevronUp, Pencil, Lock, Eye } from 'lucide-react';
import AdminNav from '../components/AdminNav';
import { useLanguage } from '../i18n/LanguageContext';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const apiFetch = async (path, options = {}) => {
  const res = await fetch(`${API_URL}${path}`, { credentials: 'include', ...options });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.detail || `Request failed (${res.status})`);
  }
  return res.json();
};

const DURATION_OPTIONS = [
  { value: 2, label: '2 minutes' },
  { value: 5, label: '5 minutes' },
  { value: 10, label: '10 minutes' },
  { value: 30, label: '30 minutes' },
  { value: 60, label: '1 hour' },
  { value: 240, label: '4 hours' },
  { value: 720, label: '12 hours' },
  { value: 1440, label: '1 day' },
  { value: 4320, label: '3 days' },
  { value: 10080, label: '1 week' },
  { value: 20160, label: '2 weeks' },
  { value: 43200, label: '30 days' },
  { value: 86400, label: '60 days' },
  { value: 129600, label: '90 days' },
];

const fmtDuration = (mins) => {
  if (mins < 60) return `${mins}m`;
  if (mins < 1440) return `${mins / 60}h`;
  if (mins < 10080) return `${Math.round(mins / 1440)}d`;
  return `${Math.round(mins / 10080)}w`;
};

const ShowroomsPage = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();

  const [showrooms, setShowrooms] = useState([]);
  const [experiences, setExperiences] = useState([]);
  const [loading, setLoading] = useState(true);

  // Create/Edit state
  const [editing, setEditing] = useState(null); // null = closed, 'new' = create, or showroom object
  const [formName, setFormName] = useState('');
  const [formExpIds, setFormExpIds] = useState([]);
  const [saving, setSaving] = useState(false);

  // Link generation
  const [linkShowroomId, setLinkShowroomId] = useState(null);
  const [linkDuration, setLinkDuration] = useState(1440);
  const [linkMaxLogins, setLinkMaxLogins] = useState(10);
  const [linkDemoMode, setLinkDemoMode] = useState(false);
  const [linkResult, setLinkResult] = useState(null);
  const [linkGenerating, setLinkGenerating] = useState(false);

  // Expanded showroom
  const [expanded, setExpanded] = useState(null);

  const [copied, setCopied] = useState(null);
  const [passcodeCopied, setPasscodeCopied] = useState(null);

  const copyPasscode = (code) => {
    navigator.clipboard.writeText(code);
    setPasscodeCopied(code);
    setTimeout(() => setPasscodeCopied(null), 2000);
  };

  const fetchAll = useCallback(async () => {
    try {
      const [sr, exp] = await Promise.all([
        apiFetch('/api/showrooms'),
        apiFetch('/api/experiences'),
      ]);
      setShowrooms(sr.items || []);
      const items = exp.items || exp;
      setExperiences(Array.isArray(items) ? items : []);
    } catch (err) {
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const getExpName = (exp) => {
    const n = exp?.name;
    if (!n) return '';
    return typeof n === 'string' ? n : (n.en || Object.values(n)[0] || '');
  };

  // ── Create / Edit ──

  const openCreate = () => {
    setEditing('new');
    setFormName('');
    setFormExpIds([]);
  };

  const openEdit = (sr) => {
    setEditing(sr);
    setFormName(sr.name);
    setFormExpIds([...sr.experience_ids]);
  };

  const closeEdit = () => { setEditing(null); };

  const toggleExp = (eid) => {
    setFormExpIds(prev => prev.includes(eid) ? prev.filter(x => x !== eid) : [...prev, eid]);
  };

  const handleSave = async () => {
    if (!formName.trim()) return;
    setSaving(true);
    try {
      if (editing === 'new') {
        await apiFetch('/api/showrooms', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: formName.trim(), experience_ids: formExpIds }),
        });
      } else {
        await apiFetch(`/api/showrooms/${editing.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: formName.trim(), experience_ids: formExpIds }),
        });
      }
      closeEdit();
      fetchAll();
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this Showroom and revoke all its links?')) return;
    try {
      await apiFetch(`/api/showrooms/${id}`, { method: 'DELETE' });
      fetchAll();
    } catch (err) {
      alert(err.message);
    }
  };

  // ── Link generation ──

  const openLinkGen = (srId) => {
    setLinkShowroomId(srId);
    setLinkDuration(1440);
    setLinkMaxLogins(10);
    setLinkDemoMode(false);
    setLinkResult(null);
  };

  const generateLink = async () => {
    setLinkGenerating(true);
    try {
      const result = await apiFetch(`/api/showrooms/${linkShowroomId}/generate-link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          duration_minutes: linkDuration,
          max_logins: linkMaxLogins,
          demo_mode: linkDemoMode,
        }),
      });
      setLinkResult(result);
      fetchAll();
    } catch (err) {
      alert(err.message);
    } finally {
      setLinkGenerating(false);
    }
  };

  const copyLink = (token, passcode, showroomName) => {
    const url = `${window.location.origin}/experience/${token}`;
    const name = showroomName || 'Tapelligence Showroom';
    if (passcode) {
      const lines = [
        name,
        '',
        'I\'d like you to take a quick look at this Tapelligence experience.',
        '',
        '👉 Open the Demo:',
        url,
        '',
        `🔐 Access Code: ${passcode}`,
      ];
      navigator.clipboard.writeText(lines.join('\n'));
    } else {
      navigator.clipboard.writeText(url);
    }
    setCopied(token);
    setTimeout(() => setCopied(null), 2000);
  };

  return (
    <>
      <AdminNav />
      <div className="min-h-screen bg-slate-50 pt-4 pb-12 px-4 sm:px-6" data-testid="showrooms-page">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-xl font-bold text-slate-800">Showrooms</h1>
              <p className="text-xs text-slate-400 mt-0.5">Custom experience collections with shareable links</p>
            </div>
            <button onClick={openCreate} data-testid="create-showroom-btn"
              className="flex items-center gap-1.5 px-4 py-2 bg-[#0077FF] text-white text-sm font-medium rounded-lg hover:bg-[#0066dd] transition-colors">
              <Plus className="w-4 h-4" /> New Showroom
            </button>
          </div>

          {/* Loading */}
          {loading && <div className="text-center text-slate-400 py-12">Loading...</div>}

          {/* Empty state */}
          {!loading && showrooms.length === 0 && (
            <div className="text-center py-16 bg-white rounded-xl border border-slate-200">
              <Link2 className="w-8 h-8 text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-slate-500">No showrooms yet</p>
              <p className="text-xs text-slate-400 mt-1">Create a showroom to bundle experiences into a single shareable link</p>
            </div>
          )}

          {/* Showroom list */}
          <div className="space-y-3">
            {showrooms.map(sr => (
              <div key={sr.id} className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid={`showroom-${sr.id}`}>
                {/* Row */}
                <div className="flex items-center gap-3 px-4 py-3">
                  <button onClick={() => setExpanded(expanded === sr.id ? null : sr.id)} className="text-slate-400 hover:text-slate-600 transition-colors">
                    {expanded === sr.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </button>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-800 truncate">{sr.name}</p>
                    <p className="text-[10px] text-slate-400 mt-0.5">
                      {sr.experience_ids?.length || 0} experience{(sr.experience_ids?.length || 0) !== 1 ? 's' : ''}
                      {sr.link_count > 0 && <> &middot; {sr.link_count} active link{sr.link_count !== 1 ? 's' : ''}</>}
                    </p>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <button onClick={() => navigate(`/admin/showroom-preview/${sr.id}`)} data-testid={`open-showroom-${sr.id}`}
                      className="flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-medium text-emerald-600 bg-emerald-50 rounded-lg hover:bg-emerald-100 transition-colors">
                      <Eye className="w-3 h-3" /> Open Showroom
                    </button>
                    <button onClick={() => openLinkGen(sr.id)} data-testid={`gen-link-${sr.id}`}
                      className="flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-medium text-[#0077FF] bg-[#0077FF]/5 rounded-lg hover:bg-[#0077FF]/10 transition-colors">
                      <ExternalLink className="w-3 h-3" /> Generate Link
                    </button>
                    <button onClick={() => openEdit(sr)} data-testid={`edit-showroom-${sr.id}`}
                      className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-50 transition-colors">
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => handleDelete(sr.id)} data-testid={`delete-showroom-${sr.id}`}
                      className="p-1.5 text-slate-400 hover:text-red-500 rounded-lg hover:bg-red-50 transition-colors">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>

                {/* Expanded: experience list */}
                {expanded === sr.id && (
                  <div className="border-t border-slate-100 px-4 py-3 bg-slate-50/50">
                    {(sr.experiences || []).length === 0 ? (
                      <p className="text-xs text-slate-400 italic">No experiences selected</p>
                    ) : (
                      <div className="flex flex-wrap gap-1.5">
                        {sr.experiences.map(e => (
                          <span key={e.id} className="inline-flex items-center px-2.5 py-1 bg-white border border-slate-200 rounded-lg text-[11px] text-slate-600 font-medium">
                            {e.name}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* ── Create/Edit Modal ── */}
        {editing && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm px-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[80vh] flex flex-col" data-testid="showroom-modal">
              <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
                <h2 className="text-base font-bold text-slate-800">
                  {editing === 'new' ? 'New Showroom' : 'Edit Showroom'}
                </h2>
                <button onClick={closeEdit} className="p-1 text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
              </div>
              <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
                <div>
                  <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1 block">Showroom Name</label>
                  <input value={formName} onChange={e => setFormName(e.target.value)} placeholder="e.g., Hotel Showroom"
                    data-testid="showroom-name-input"
                    className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-[#0077FF]/30" />
                </div>
                <div>
                  <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-2 block">
                    Select Experiences ({formExpIds.length} selected)
                  </label>
                  <div className="space-y-1.5 max-h-60 overflow-y-auto">
                    {experiences.map(exp => {
                      const eid = exp.id || exp._id;
                      const selected = formExpIds.includes(eid);
                      return (
                        <button key={eid} onClick={() => toggleExp(eid)} data-testid={`exp-toggle-${eid}`}
                          className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border text-left transition-colors ${
                            selected ? 'border-[#0077FF]/30 bg-[#0077FF]/5' : 'border-slate-200 bg-white hover:bg-slate-50'
                          }`}>
                          <div className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                            selected ? 'border-[#0077FF] bg-[#0077FF]' : 'border-slate-300'
                          }`}>
                            {selected && <Check className="w-2.5 h-2.5 text-white" />}
                          </div>
                          <span className="text-sm text-slate-700 font-medium truncate">{getExpName(exp)}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-slate-100">
                <button onClick={closeEdit} className="px-4 py-2 text-sm text-slate-500 hover:text-slate-700">Cancel</button>
                <button onClick={handleSave} disabled={saving || !formName.trim()}
                  data-testid="showroom-save-btn"
                  className="px-5 py-2 bg-[#0077FF] text-white text-sm font-medium rounded-lg hover:bg-[#0066dd] transition-colors disabled:opacity-50">
                  {saving ? 'Saving...' : editing === 'new' ? 'Create Showroom' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Generate Link Modal ── */}
        {linkShowroomId && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm px-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-md" data-testid="showroom-link-modal">
              <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
                <h2 className="text-base font-bold text-slate-800">Generate Showroom Link</h2>
                <button onClick={() => { setLinkShowroomId(null); setLinkResult(null); }}
                  className="p-1 text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
              </div>
              <div className="px-5 py-4 space-y-4">
                {!linkResult ? (
                  <>
                    <div>
                      <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1 block">Session Duration</label>
                      <select value={linkDuration} onChange={e => setLinkDuration(Number(e.target.value))}
                        data-testid="showroom-link-duration"
                        className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-[#0077FF]/30">
                        {DURATION_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="flex items-end pb-0.5">
                        <div className="flex items-center justify-between w-full">
                          <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Demo Mode</label>
                          <button type="button" onClick={() => setLinkDemoMode(!linkDemoMode)}
                            data-testid="showroom-link-demo-mode"
                            className={`relative w-10 h-5 rounded-full transition-colors ${linkDemoMode ? 'bg-amber-500' : 'bg-slate-300'}`}>
                            <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${linkDemoMode ? 'translate-x-5' : 'translate-x-0.5'}`} />
                          </button>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Lock className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" />
                        <p className="text-[10px] text-blue-600">Passcode protected</p>
                      </div>
                    </div>
                    <button onClick={generateLink} disabled={linkGenerating}
                      data-testid="showroom-generate-link-btn"
                      className="w-full py-2.5 bg-[#0077FF] text-white text-sm font-medium rounded-lg hover:bg-[#0066dd] transition-colors disabled:opacity-50">
                      {linkGenerating ? 'Generating...' : 'Generate Link'}
                    </button>
                  </>
                ) : (
                  <div className="text-center py-2">
                    <div className="w-10 h-10 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-3">
                      <Check className="w-5 h-5 text-emerald-600" />
                    </div>
                    <p className="text-sm font-semibold text-slate-800 mb-1">Link Generated</p>
                    <p className="text-[10px] text-slate-400 mb-2">
                      {fmtDuration(linkResult.session_duration_minutes)} session &middot; Passcode protected
                    </p>
                    {linkResult.passcode && (
                      <button onClick={() => copyPasscode(linkResult.passcode)} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 border border-blue-200 rounded-xl mb-3 cursor-pointer hover:bg-blue-100 transition-colors" data-testid="showroom-passcode-copy-btn">
                        <Lock className="w-4 h-4 text-blue-500" />
                        <span className="text-lg font-bold text-blue-800 tracking-[0.3em] font-mono" data-testid="showroom-passcode-display">
                          {passcodeCopied === linkResult.passcode ? 'Copied!' : linkResult.passcode}
                        </span>
                      </button>
                    )}
                    <div className="mt-2 flex items-center justify-center gap-2">
                      <button onClick={() => {
                        const sr = showrooms.find(s => s.id === linkShowroomId);
                        copyLink(linkResult.token, linkResult.passcode, sr?.name);
                      }}
                        data-testid="showroom-copy-link-btn"
                        className="inline-flex items-center gap-1.5 px-4 py-2 bg-slate-100 hover:bg-slate-200 text-sm font-medium text-slate-700 rounded-lg transition-colors">
                        {copied === linkResult.token ? <><Check className="w-3.5 h-3.5 text-emerald-600" /> Demo details copied</> : <><Copy className="w-3.5 h-3.5" /> Copy Demo Details</>}
                      </button>
                      {typeof navigator !== 'undefined' && navigator.share && (
                        <button onClick={() => {
                          const sr = showrooms.find(s => s.id === linkShowroomId);
                          const name = sr?.name || 'Tapelligence Showroom';
                          const url = `${window.location.origin}/experience/${linkResult.token}`;
                          const lines = [name, '', 'I\'d like you to take a quick look at this Tapelligence experience.', '', '👉 Open the Demo:', url];
                          if (linkResult.passcode) { lines.push(''); lines.push(`🔐 Access Code: ${linkResult.passcode}`); }
                          navigator.share({ title: name, text: lines.join('\n') }).catch(() => {});
                        }}
                          data-testid="showroom-share-link-btn"
                          className="inline-flex items-center gap-1.5 px-4 py-2 bg-slate-100 hover:bg-slate-200 text-sm font-medium text-slate-700 rounded-lg transition-colors">
                          <ExternalLink className="w-3.5 h-3.5" /> Share
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default ShowroomsPage;
